import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAppStatusTransitions } from "@/hooks/queries";
import { useBrokersQuery } from "@/hooks/queries/useBrokersQuery";
import { queryKeys } from "@/queryKeys";
import { underwriterService } from "@/services/underwriters";
import { US_STATES } from "@/lib/usStates";
import { RootState } from "@/store/store";
import { CREATED_PRESETS } from "./createdFilter";
import { FilterOptionsSource, SearchFilter } from "./filterRegistry";

export interface FilterOption {
  /** What accepting the option inserts. */
  value: string;
  label: string;
  detail?: string;
  /** Texts typed input is matched against. */
  keywords: string[];
  /** Opens the range calendar instead of inserting a value. */
  opensCalendar?: boolean;
}

export interface FilterOptions {
  options: FilterOption[];
  /** The server holds more matches than the fetched page: typing narrows to them. */
  hasMore: boolean;
}

interface FetchedPage {
  count: number;
  results: unknown[];
}

const SEARCH_DEBOUNCE_MS = 250;
const PEOPLE_STALE_MS = 30_000;

/** Sources searched on the server: their lists are one page, so the sheet offers a search box. */
export const isRemoteSource = (source: FilterOptionsSource) =>
  source === "brokers" || source === "underwriters";

const STATE_OPTIONS: FilterOption[] = US_STATES.map(({ code, name }) => ({
  value: code,
  label: code,
  detail: name,
  keywords: [code, name],
}));

const CREATED_OPTIONS: FilterOption[] = [
  ...CREATED_PRESETS.map(({ value, label }) => ({ value, label, keywords: [label] })),
  { value: "", label: "Custom range", keywords: ["Custom range"], opensCalendar: true },
];

/** Brokers get "independent" as a placeholder company, which names no ISO. */
function isRealCompany(name: string | undefined): name is string {
  const company = name?.trim().toLowerCase();
  return Boolean(company) && company !== "independent";
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Every value `filter` suggests for the typed `query`, before narrowing. People
 * lists are fetched (debounced) from the endpoints the viewer can already read,
 * so they carry that endpoint's role scoping.
 */
export function useFilterOptions(filter: SearchFilter | undefined, query: string): FilterOptions {
  const source: FilterOptionsSource = filter?.options ?? "none";
  const search = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

  const { choices } = useAppStatusTransitions({ enabled: source === "status" });
  // The hook hands out a fresh empty array while disabled; keep it out of the memo.
  const statusChoices = source === "status" ? choices : undefined;
  const brokers = useBrokersQuery(search, 1, null, {
    enabled: source === "brokers",
    keepPrevious: true,
  });
  const underwriters = useQuery({
    queryKey: queryKeys.underwriterSearch(search),
    queryFn: () => underwriterService.list({ search }),
    enabled: source === "underwriters",
    staleTime: PEOPLE_STALE_MS,
    placeholderData: keepPreviousData,
  });
  const organizations = useSelector((state: RootState) => state.auth.user?.organizations);

  const options = useMemo(() => {
    const options: Record<FilterOptionsSource, () => FilterOption[]> = {
      status: () =>
        (statusChoices ?? []).map(({ value, label }) => ({
          value,
          label,
          keywords: [label, value],
        })),
      brokers: () =>
        (brokers.data?.results ?? []).map(({ email, company_name }) =>
          isRealCompany(company_name)
            ? {
                value: company_name,
                label: company_name,
                detail: email,
                keywords: [company_name, email],
              }
            : { value: email, label: email, keywords: [email] }
        ),
      underwriters: () =>
        (underwriters.data?.results ?? []).map(({ email }) => ({
          value: email,
          label: email,
          keywords: [email],
        })),
      states: () => STATE_OPTIONS,
      created: () => CREATED_OPTIONS,
      organizations: () =>
        (organizations ?? []).map(({ name }) => ({ value: name, label: name, keywords: [name] })),
      none: () => [],
    };
    return dedupeByValue(options[source]());
  }, [source, statusChoices, brokers.data, underwriters.data, organizations]);

  // From the same (kept) response as the rows, so the two never disagree.
  const pages: Partial<Record<FilterOptionsSource, FetchedPage | undefined>> = {
    brokers: brokers.data,
    underwriters: underwriters.data,
  };
  const page = pages[source];
  return { options, hasMore: page !== undefined && page.count > page.results.length };
}

function dedupeByValue(options: FilterOption[]): FilterOption[] {
  const seen = new Set<string>();
  return options.filter((option) => {
    const key = option.value.toLowerCase();
    if (option.opensCalendar || !seen.has(key)) {
      seen.add(key);
      return true;
    }
    return false;
  });
}

/**
 * The options left after narrowing by the typed `query` (prefix or substring,
 * per the filter) and dropping values the token already holds.
 */
export function narrowOptions(
  options: FilterOption[],
  query: string,
  match: SearchFilter["match"],
  taken: string[]
): FilterOption[] {
  const needle = query.trim().toLowerCase();
  const takenSet = new Set(taken.map((value) => value.toLowerCase()));
  const matches = (text: string) => {
    const haystack = text.toLowerCase();
    return match === "prefix" ? haystack.startsWith(needle) : haystack.includes(needle);
  };
  return options.filter(
    (option) =>
      !takenSet.has(option.value.toLowerCase()) && (!needle || option.keywords.some(matches))
  );
}
